import { useCallback, useMemo } from 'react';

export interface SequentialSource<T> {
  key: string;
  items: T[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  fetchMore: () => void;
}

export interface UseSequentialSourceParams<T> {
  sources: SequentialSource<T>[]; // The order of the sources is important
  getIdentifier: (item: T) => string | number; // Used to remove duplicates
}

export const useSequentialSources = <T>({
  sources,
  getIdentifier,
}: UseSequentialSourceParams<T>) => {
  const activeSourceIndex = useMemo(() => {
    return sources.findIndex((source) => source.hasMore);
  }, [sources]);

  const activeSourceKey =
    activeSourceIndex !== -1 ? sources[activeSourceIndex].key : undefined;

  const hasMore = activeSourceIndex !== -1;

  // Safe concatenation and dedupe
  const data = useMemo(() => {
    const seenIds = new Set<string | number>();
    const safeData: T[] = [];

    const limit = sources.length;

    for (let i = 0; i < limit; i++) {
      const source = sources[i];

      for (const item of source.items) {
        const id = getIdentifier(item);

        if (!seenIds.has(id)) {
          seenIds.add(id);
          safeData.push(item);
        }
      }
    }

    return safeData;
  }, [getIdentifier, sources]);

  const loadMore = useCallback(() => {
    if (activeSourceIndex !== -1) {
      sources[activeSourceIndex].fetchMore();
    }
  }, [activeSourceIndex, sources]);

  const isSuccess = sources.some((source) => source.isSuccess);
  const isError = sources.every((source) => source.isError);
  const isLoading =
    !isSuccess && !isError && sources.some((source) => source.isLoading);

  const isFetchingMore = sources.some((source) => source.isFetchingMore);

  return {
    data,
    isLoading,
    isSuccess,
    isError,
    hasMore,
    isFetchingMore,
    loadMore,
    activeSourceKey,
  };
};
